import { z } from 'zod';
import { decimalString } from './common';

/**
 * Schemas for project-level and per-cabinet Hardware line items.
 *
 * Paths:
 * - `/projects/:projectId/hardware`
 * - `/projects/:projectId/cabinets/:cabinetId/hardware`
 *
 * Cabinet hardware keeps contributing to its parent cabinet. Project-level
 * hardware is a standalone line item using the same product/quantity fields.
 *
 * Snapshot semantics:
 * - When created `from-catalog`, the line item copies the catalog template
 *   fields and restores the linked Hardware product, quantity, coefficient,
 *   unit price, and currency when those were captured in catalog attributes.
 * - `catalogItemId` is recorded for traceability and is **immutable** once set.
 * - Subsequent edits to the catalog item do NOT propagate to existing lines.
 *
 * Worker access:
 * - Workers may LIST/GET hardware only on projects they are assigned to.
 * - Workers see no `unitPriceNet`/`currency` (financials hidden).
 * - Workers may not mutate (`PROJECT_ITEMS_CREATE/UPDATE/DELETE`).
 *
 * Status locking:
 * - Mutations are forbidden when the parent project is in a terminal status
 *   (`ACCEPTED` or `REJECTED` in terminal states).
 */

const positiveDecimal = decimalString.refine((v) => parseFloat(v) > 0, {
  message: 'Must be greater than 0',
});

const positiveIntegerDecimal = decimalString.refine(
  (v) => Number.isInteger(Number(v)) && Number(v) > 0,
  {
    message: 'Must be a positive integer',
  },
);

const nonNegativeDecimal = decimalString.refine((v) => parseFloat(v) >= 0, {
  message: 'Must be ≥ 0',
});

const hardwareCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type HardwareCurrencyValue = z.infer<typeof hardwareCurrencyEnum>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

export const hardwareListItem = z.object({
  id: z.string(),
  projectId: z.string(),
  roomId: z.string().cuid().nullable(),
  areaId: z.string().cuid().nullable(),
  cabinetId: z.string().nullable(),
  catalogItemId: z.string().nullable(),
  hardwareCatalogId: z.string().nullable(),
  hardwareCatalogName: z.string().nullable(),
  hardwareCatalogSku: z.string().nullable(),
  hardwareCatalogManufacturer: z.string().nullable(),
  name: z.string(),
  sku: z.string().nullable(),
  finish: z.string().nullable(),
  material: z.string().nullable(),
  quantity: decimalString,
  lengthM: decimalString.optional(),
  coefficient: decimalString,
  // Financial fields are absent for callers without PROJECTS_READ_FINANCIALS.
  unitPriceNet: decimalString.optional(),
  /**
   * Unit price WITHOUT the markup: MANUAL rows repeat `unitPriceNet`; AUTO rows
   * report the engine's catalog-cost sum (`subtotalBaseNet`), absent until a
   * breakdown stored before that field existed is recomputed. Absent when
   * finances are hidden.
   */
  unitPriceBaseNet: decimalString.optional(),
  /**
   * Unit price WITH the markup: `unitPriceNet` times the row coefficient. On
   * AUTO rows the markups already live inside `unitPriceNet`, so the two agree
   * there by construction. Absent when finances are hidden.
   */
  unitPriceFinalNet: decimalString.optional(),
  currency: hardwareCurrencyEnum.optional(),
  // Server-computed: quantity * coefficient * unitPriceNet. Absent when finances hidden.
  totalNet: decimalString.optional(),
  orderIndex: z.number().int(),
  notes: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type HardwareListItem = z.infer<typeof hardwareListItem>;

export const hardwareDetails = hardwareListItem;
export type HardwareDetails = z.infer<typeof hardwareDetails>;

export const listHardwareResponse = z.array(hardwareListItem);
export type ListHardwareResponse = z.infer<typeof listHardwareResponse>;

// ----- Manual create, optionally linked to the dedicated Hardware catalog -----
export const createHardwareRequest = z.object({
  roomId: z.string().cuid().optional().nullable(),
  areaId: z.string().cuid().optional().nullable(),
  hardwareCatalogId: z.string().cuid().optional().nullable(),
  name: z.string().trim().min(1).max(200),
  sku: optionalTrim(50),
  finish: optionalTrim(100),
  material: optionalTrim(100),
  quantity: positiveIntegerDecimal.default('1'),
  lengthM: nonNegativeDecimal.optional(),
  coefficient: positiveDecimal.default('2.5'),
  unitPriceNet: nonNegativeDecimal.default('0.00'),
  currency: hardwareCurrencyEnum.default('RON'),
  notes: optionalTrim(2000),
});
export type CreateHardwareRequest = z.infer<typeof createHardwareRequest>;

// ----- Create from a CatalogItem (snapshot) -----
//
// Catalog hardware templates can carry product/quantity/coefficient snapshots.
// Request-level values remain optional overrides for older templates.
export const createHardwareFromCatalogRequest = z.object({
  catalogItemId: z.string().min(1),
  roomId: z.string().cuid().optional().nullable(),
  areaId: z.string().cuid().optional().nullable(),
  unitPriceNet: nonNegativeDecimal.optional(),
  currency: hardwareCurrencyEnum.optional(),
  quantity: positiveIntegerDecimal.optional(),
  coefficient: positiveDecimal.optional(),
  notes: optionalTrim(2000),
  overrides: z
    .object({
      name: z.string().trim().min(1).max(200).optional(),
      sku: optionalTrim(50),
      finish: optionalTrim(100),
      material: optionalTrim(100),
      hardwareCatalogId: z.string().cuid().optional().nullable(),
    })
    .optional(),
});
export type CreateHardwareFromCatalogRequest = z.infer<typeof createHardwareFromCatalogRequest>;

// ----- Update (partial) -----
//
// `catalogItemId` is intentionally not patchable — the snapshot link is
// permanent. `cabinetId` is taken from the URL.
export const updateHardwareRequest = z
  .object({
    roomId: z.string().cuid().nullable(),
    areaId: z.string().cuid().nullable(),
    name: z.string().trim().min(1).max(200),
    sku: optionalTrim(50),
    finish: optionalTrim(100),
    material: optionalTrim(100),
    hardwareCatalogId: z.string().cuid().nullable(),
    quantity: positiveIntegerDecimal,
    lengthM: nonNegativeDecimal,
    coefficient: positiveDecimal,
    unitPriceNet: nonNegativeDecimal,
    currency: hardwareCurrencyEnum,
    orderIndex: z.number().int().min(0),
    notes: optionalTrim(2000),
  })
  .partial();
export type UpdateHardwareRequest = z.infer<typeof updateHardwareRequest>;
